import {
	FileProductCalculatedValues,
	FileProductInputLike,
	FileProductOptionCalculatedValues,
	FileProductOptionInputLike,
	FixDiscountLike,
	FixDiscountOutput,
} from "./types";

import { bigMath } from "../helpers";
import Big from "big.js";

import { DISCOUNT_TYPE, FIX_DISCOUNT_TYPE } from "~served/configs/enums";

import { FixDiscountInput } from "./types";

export const getFixDiscountAmount = (
	originalAmount: number,
	fixDiscountInput: FixDiscountInput,
) => {
	let fixDiscountAmount = 0;

	if (fixDiscountInput.type === DISCOUNT_TYPE.AMOUNT) {
		fixDiscountAmount = fixDiscountInput.value;
	}

	if (fixDiscountInput.type === DISCOUNT_TYPE.PERCENTAGE) {
		fixDiscountAmount = new Big(originalAmount)
			.mul(fixDiscountInput.value)
			.div(100)
			.round(2)
			.toNumber();
	}

	const fixDiscountedAmount = new Big(originalAmount)
		.minus(fixDiscountAmount)
		.round(2)
		.toNumber();

	return { fixDiscountAmount, fixDiscountedAmount };
};

export const getAppliedFixDiscountByProducts = <
	TFileProduct extends
		FileProductInputLike<FileProductCalculatedValues> = FileProductInputLike<FileProductCalculatedValues>,
	TFileProductOption extends
		FileProductOptionInputLike<FileProductOptionCalculatedValues> = FileProductOptionInputLike<FileProductOptionCalculatedValues>,
>(
	products: FileProductInputLike<TFileProduct, TFileProductOption>[],
	fixDiscount: FixDiscountLike,
) => {
	const eligibleProducts: Record<string, FixDiscountOutput> = {};

	if (!fixDiscount.configs.length)
		return { fixDiscount: null, eligibleProducts, fixDiscountAmount: 0 };

	if (isOrderFixDiscount(fixDiscount))
		return { fixDiscount: null, eligibleProducts, fixDiscountAmount: 0 };

	products.forEach((fileProduct) => {
		const productTotalPrice = fileProduct.subtotal;

		fixDiscount.configs.forEach((config) => {
			switch (config.type) {
				case FIX_DISCOUNT_TYPE.PRODUCTS:
					if (
						config.products.map(String).includes(fileProduct.product.toString())
					) {
						const { fixDiscountAmount } = getFixDiscountAmount(
							productTotalPrice,
							{
								type: config.discountType,
								value: config.discountValue,
							},
						);

						if (!eligibleProducts[fileProduct._id.toString()]) {
							eligibleProducts[fileProduct._id.toString()] = {
								type: config.discountType,
								value: config.discountValue,
								amount:
									fixDiscountAmount > productTotalPrice
										? productTotalPrice
										: fixDiscountAmount,
								fixDiscount: fixDiscount._id,
							};
						} else if (eligibleProducts[fileProduct._id.toString()]) {
							if (
								(fixDiscount.isOfferingHigherValue &&
									fixDiscountAmount >
										eligibleProducts[fileProduct._id.toString()].value) ||
								(!fixDiscount.isOfferingHigherValue &&
									fixDiscountAmount <
										eligibleProducts[fileProduct._id.toString()].value)
							) {
								eligibleProducts[fileProduct._id.toString()] = {
									type: config.discountType,
									value: config.discountValue,
									amount:
										fixDiscountAmount > productTotalPrice
											? productTotalPrice
											: fixDiscountAmount,
									fixDiscount: fixDiscount._id,
								};
							}
						}
					}
					break;
				case FIX_DISCOUNT_TYPE.CATEGORIES:
					if (
						config.categories
							.map(String)
							.includes(fileProduct.category.toString())
					) {
						const { fixDiscountAmount } = getFixDiscountAmount(
							productTotalPrice,
							{
								type: config.discountType,
								value: config.discountValue,
							},
						);

						if (!eligibleProducts[fileProduct._id.toString()]) {
							eligibleProducts[fileProduct._id.toString()] = {
								type: config.discountType,
								value: config.discountValue,
								amount:
									fixDiscountAmount > productTotalPrice
										? productTotalPrice
										: fixDiscountAmount,
								fixDiscount: fixDiscount._id,
							};
						} else if (eligibleProducts[fileProduct._id.toString()]) {
							if (
								(fixDiscount.isOfferingHigherValue &&
									fixDiscountAmount >
										eligibleProducts[fileProduct._id.toString()].value) ||
								(!fixDiscount.isOfferingHigherValue &&
									fixDiscountAmount <
										eligibleProducts[fileProduct._id.toString()].value)
							) {
								eligibleProducts[fileProduct._id.toString()] = {
									type: config.discountType,
									value: config.discountValue,
									amount:
										fixDiscountAmount > productTotalPrice
											? productTotalPrice
											: fixDiscountAmount,
									fixDiscount: fixDiscount._id,
								};
							}
						}
					}
					break;
				case FIX_DISCOUNT_TYPE.PRODUCT_TYPES:
					if (config.types.map(String).includes(fileProduct.type.toString())) {
						const { fixDiscountAmount } = getFixDiscountAmount(
							fileProduct.subtotal,
							{
								type: config.discountType,
								value: config.discountValue,
							},
						);

						if (!eligibleProducts[fileProduct._id.toString()]) {
							eligibleProducts[fileProduct._id.toString()] = {
								type: config.discountType,
								value: config.discountValue,
								amount:
									fixDiscountAmount > productTotalPrice
										? productTotalPrice
										: fixDiscountAmount,
								fixDiscount: fixDiscount._id,
							};
						} else if (eligibleProducts[fileProduct._id.toString()]) {
							if (
								(fixDiscount.isOfferingHigherValue &&
									fixDiscountAmount >
										eligibleProducts[fileProduct._id.toString()].value) ||
								(!fixDiscount.isOfferingHigherValue &&
									fixDiscountAmount <
										eligibleProducts[fileProduct._id.toString()].value)
							) {
								eligibleProducts[fileProduct._id.toString()] = {
									type: config.discountType,
									value: config.discountValue,
									amount:
										fixDiscountAmount > productTotalPrice
											? productTotalPrice
											: fixDiscountAmount,
									fixDiscount: fixDiscount._id,
								};
							}
						}
					}
					break;
			}
		});
	});

	return {
		fixDiscount,
		eligibleProducts,
		fixDiscountAmount: Object.values(eligibleProducts).reduce(
			(pre, cur) => bigMath.add(pre, cur.value),
			0,
		),
	};
};

export const getAppliedFixDiscountByReceipt = (
	amount: number,
	fixDiscount: FixDiscountLike,
) => {
	let discountAmount = 0;

	if (!fixDiscount.configs.length || !amount)
		return {
			fixDiscount: fixDiscount ?? null,
			fixDiscountAmount: discountAmount,
		};

	if (!isOrderFixDiscount(fixDiscount))
		return { fixDiscount: null, fixDiscountAmount: discountAmount };

	fixDiscount.configs.forEach((config) => {
		const { fixDiscountAmount } = getFixDiscountAmount(amount, {
			type: config.discountType,
			value: config.discountValue,
		});
		if (
			(fixDiscount.isOfferingHigherValue &&
				fixDiscountAmount > fixDiscountAmount) ||
			(!fixDiscount.isOfferingHigherValue &&
				fixDiscountAmount < fixDiscountAmount)
		) {
			discountAmount = fixDiscountAmount;
		}
	});

	return {
		fixDiscount,
		fixDiscountAmount: discountAmount,
	};
};

export const isOrderFixDiscount = (fixDiscount: FixDiscountLike) => {
	return fixDiscount.configs.some(
		(config) =>
			config.type === FIX_DISCOUNT_TYPE.ORDERS ||
			config.type === FIX_DISCOUNT_TYPE.AGENTS ||
			config.type === FIX_DISCOUNT_TYPE.CLIENTS,
	);
};
